import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { termsApi } from '../utils/api';
import { hasAdminRights } from '../utils/roles';
import { LoaderCircleIcon } from './icons/LucideIcons';
import { AuthError, AUTH_LINK } from './AuthLayout';

/**
 * The clickwrap: Bouqmark's Terms of Service, DPA and the Stripe Connected
 * Account Agreement. A shop's account is provisioned by Bouqmark; the owner
 * finishes creating it here on their first sign-in, and nobody in the shop
 * gets into the app until the box is ticked (GET/POST /api/terms-acceptance).
 *
 * The box starts unticked and the button stays disabled until it is ticked;
 * the server refuses anything else too. Billing-exempt shops (Twigs) are never
 * asked (`required` is false). If the status can't be loaded the app opens:
 * the server still refuses Stripe onboarding until the shop has accepted.
 */
const TermsGate = ({ children }) => {
  const { user, logout } = useAuth();
  const [status, setStatus] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await termsApi.get();
      setStatus(r?.success ? r.data : null);
    } catch (err) {
      setStatus(null);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!loaded) {
    return (
      <div className="min-h-screen bg-surface-alt flex items-center justify-center text-floral-700">
        <LoaderCircleIcon size={28} className="animate-spin" />
      </div>
    );
  }

  if (!status || !status.required) return <>{children}</>;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreed) {
      setError('Tick the box to agree before you continue.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const r = await termsApi.accept(status.current);
      if (r?.success) setStatus(r.data);
      else setError(r?.message || 'Could not save. Please try again.');
    } catch (err) {
      setError(err.message || 'Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const { links } = status;
  const link = (href, text) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className={AUTH_LINK}>{text}</a>
  );

  // Dialog pattern on the page ground: 16px radius, title 17px/600, 13px body,
  // actions right-aligned with one primary.
  return (
    <div className="min-h-screen bg-surface-alt px-4 py-12 text-gray-900">
      <div className="mx-auto flex max-w-md flex-col gap-6">
        <div className="flex items-center justify-center gap-2.5">
          <div className="grid h-7 w-7 place-items-center rounded-md bg-floral-700 text-sm font-bold text-white" aria-hidden="true">B</div>
          <span className="text-base font-semibold tracking-[-0.01em]">Bouqmark</span>
        </div>

        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-lg">
          <h1 className="text-[17px] font-semibold leading-6 tracking-[-0.01em] text-gray-900">Finish creating your account</h1>
          {hasAdminRights(user) ? (
            <form onSubmit={handleSubmit} className="mt-4 flex flex-col gap-4">
              <label htmlFor="terms-agree" className="flex items-start gap-3 text-[13px] text-gray-600">
                <input
                  id="terms-agree"
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => { setAgreed(e.target.checked); setError(''); }}
                  className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-floral-700 focus:ring-floral-700"
                />
                <span>
                  I agree. By creating your account, you agree to the Bouqmark{' '}
                  {link(links.terms, 'Terms of Service')} and{' '}
                  {link(links.dpa, 'Data Processing Agreement')} and the{' '}
                  {link(links.stripe, 'Stripe Connected Account Agreement')}.
                </span>
              </label>

              {error && <AuthError>{error}</AuthError>}

              <div className="mt-1 flex justify-end gap-2">
                <button type="button" onClick={logout} className="btn-secondary">
                  Sign out
                </button>
                <button type="submit" className="btn-primary" disabled={!agreed || saving}>
                  {saving ? 'Creating account…' : 'Create account'}
                </button>
              </div>
            </form>
          ) : (
            <>
              <p className="mt-1.5 text-[13px] text-gray-600">
                Your shop's owner needs to accept the Bouqmark terms before the app can be used.
                Ask them to sign in, then try again.
              </p>
              <div className="mt-5 flex justify-end">
                <button type="button" onClick={logout} className="btn-secondary">
                  Sign out
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TermsGate;
